import { useState, useEffect, useRef } from "react";
import styles from "./topbar.module.css";
import { Logo } from "../../../../constants";
import {
  IconArrowRight,
  IconBell,
  IconMenu2,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import { VerifiedIcon } from "../../../../constants/svg";
import Image from "../../../../assets/svgs/companyAvatar.svg";
import { errorHandler } from "../../../../utils";
import { getNotifications } from "../../../../services/clientCalls";
import Notifications from "../../../NotificationDropdown/notificationDropdown";

const Header = ({
  sidebarMobileOpen,
  sidebarCollapsed,
  mobileSearchOpen,
  onSidebarToggle,
  onMobileSearchToggle,
  onMobileSearchClose,
  data,
}) => {
  const navigate = useNavigate();

  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 730);
  const mobileSearchInputRef = useRef(null);

  useEffect(() => {
    const handler = () => {
      setIsMobileView(window.innerWidth <= 768);
      setIsMobile(window.innerWidth <= 730);
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    if (mobileSearchOpen && mobileSearchInputRef.current) {
      setTimeout(() => mobileSearchInputRef.current?.focus(), 50);
    }
  }, [mobileSearchOpen]);

  useEffect(() => {
    if (!mobileSearchOpen) return;
    const handler = (e) => {
      const panel = document.getElementById("mobileSearchPanel");
      const btn = document.getElementById("mobileSearchBtn");
      if (
        panel &&
        !panel.contains(e.target) &&
        btn &&
        !btn.contains(e.target)
      ) {
        onMobileSearchClose();
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [mobileSearchOpen, onMobileSearchClose]);

  const ToggleIcon = isMobileView
    ? sidebarMobileOpen
      ? IconX
      : IconMenu2
    : sidebarCollapsed
      ? IconMenu2
      : IconX;

  const [notifOpen, setNotifOpen] = useState(false);
  const [rawNotifData, setRawNotifData] = useState([]);
  const [hasUnread, setHasUnread] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [seenAt, setSeenAt] = useState(() => {
    const stored = localStorage.getItem("clientNotificationsSeenAt");
    return stored ? parseInt(stored, 10) : Date.now();
  });

  const updateCounts = (notifs, baseline) => {
    const unread = notifs?.filter((n) => !n?.isRead);
    const newlyArrived = notifs?.filter(
      (n) => !n?.isRead && new Date(n?.createdAt).getTime() > baseline,
    );
    setUnreadCount(unread?.length);
    setNewCount(newlyArrived?.length);
    setHasUnread(unread?.length > 0 || newlyArrived?.length > 0);
  };

  const groupJobApplications = (notifications) => {
    const jobApps = notifications?.filter((n) =>
      n?.title?.toLowerCase()?.includes("talent applied for job"),
    );
    if (jobApps?.length === 0) return [];

    return [
      {
        ...jobApps[0],
        _id: `grouped-${jobApps[0]?._id}`,
        title: "Job Match Found",
        // isHtml flag tells Notifications to use dangerouslySetInnerHTML
        displayMessage:
          jobApps?.length === 1
            ? `Hola! You have just matched with <u style="cursor:pointer">a talent</u>`
            : `Hola! You have just matched with <u style="cursor:pointer">some talents</u>`,
        navLink: "/talent?tab=matches&view=list",
        isHtml: true,
      },
    ];
  };

  const formatClientNotificationMessage = (notification) => {
    const { title, message } = notification;
    const titleLower = title?.toLowerCase() || "";
    if (notification?.navLink) {
      return {
        displayMessage: notification?.displayMessage,
        navLink: notification?.navLink,
        isHtml: notification?.isHtml ?? false,
      };
    }

    if (titleLower.includes("matched with some talents"))
      return {
        displayMessage: "Hola! You have just matched with some talents",
        navLink: "/client/talents?tab=matches",
        isHtml: false,
      };

    if (titleLower.includes("talent applied for job"))
      return {
        displayMessage: "Hola! You have just matched with a talent",
        navLink: "/talent?tab=matches&view=list",
        isHtml: false,
      };

    if (titleLower.includes("match was cancelled"))
      return {
        displayMessage:
          "Your match was cancelled. Since you have paid you have the option of matching with a talent, this expires in the next 48 hours",
        navLink: "/client/talents?tab=matches",
        isHtml: false,
      };

    if (titleLower.includes("payment was successful"))
      return {
        displayMessage:
          "Your payment was successful. You can now match with 5 talents, valid for 1 year",
        navLink: "/client/talents?tab=matches",
        isHtml: false,
      };

    if (titleLower.includes("job has been successfully completed"))
      return {
        displayMessage: "Your job has been successfully completed",
        navLink: "/client/jobs?tab=completed",
        isHtml: false,
      };

    if (titleLower.includes("welcome to awasource"))
      return {
        displayMessage:
          "Welcome to Awasource! We're excited to have you on board.",
        navLink: null,
        isHtml: false,
      };

    if (titleLower.includes("subscription successful"))
      return {
        displayMessage:
          "Subscription successful. You now have full access to all features",
        navLink: "/subscription-payment",
        isHtml: false,
      };

    if (titleLower.includes("subscription cancelled"))
      return {
        displayMessage: "Your subscription has been cancelled",
        navLink: "/subscription-payment",
        isHtml: false,
      };

    if (titleLower.includes("free trial expired"))
      return {
        displayMessage:
          "Your free trial has expired. Upgrade to continue using premium features",
        navLink: "/subscription-payment",
        isHtml: false,
      };

    return {
      displayMessage:
        message?.replace(/<[^>]*>/g, "") ||
        title?.replace(/<[^>]*>/g, "") ||
        "You have a new notification",
      navLink: notification?.actionUrl || "/client-activity-summary",
      isHtml: false,
    };
  };

  const buildProcessedNotifs = (rawNotifs) => {
    const grouped = groupJobApplications(rawNotifs);
    const others = rawNotifs?.filter(
      (n) => !n?.title?.toLowerCase()?.includes("talent applied for job"),
    );
    return [...grouped, ...others]?.map((n) => ({
      ...n,
      ...formatClientNotificationMessage(n),
    }));
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getNotifications();
        const notifs = res?.data?.data?.notifications ?? [];
        setRawNotifData(notifs);
        updateCounts(notifs, seenAt);
      } catch (error) {
        error?.response?.data
          ? errorHandler(error?.response?.data?.errors, "Error")
          : errorHandler(
              "Network error: Something went wrong, please check your internet connection and try again.",
              "Error",
            );
      }
    };
    fetch();
  }, []);

  const handleBellClick = () => {
    const now = Date.now();
    setSeenAt(now);
    localStorage.setItem("clientNotificationsSeenAt", now.toString());

    if (isMobile) {
      setHasUnread(false);
      setUnreadCount(0);
      setNewCount(0);
      navigate("/client-activity-summary?onlyNotifications=true");
    } else {
      setNotifOpen((prev) => {
        if (!prev) {
          setHasUnread(false);
          setUnreadCount(0);
          setNewCount(0);
        }
        return !prev;
      });
    }
  };

  const handleNotifClick = (id, navLink) => {
    setRawNotifData((prev) =>
      prev?.map((n) => (n?._id === id ? { ...n, isRead: true } : n)),
    );
    setRawNotifData((prev) => {
      const stillUnread = prev?.some((n) => !n?.isRead);
      setHasUnread(stillUnread);
      return prev;
    });
    if (navLink) {
      setNotifOpen(false);
      navigate(navLink);
    }
  };

  const handleMarkAllRead = () => {
    setRawNotifData((prev) => prev?.map((n) => ({ ...n, isRead: true })));
    setHasUnread(false);
    setUnreadCount(0);
    setNewCount(0);
  };

  const handleSeeAll = () => {
    setNotifOpen(false);
    navigate("/client-activity-summary");
  };

  const processedNotifs = buildProcessedNotifs(rawNotifData);

  const handleSearchTalent = (value) => {
    const query = value?.trim() ?? "";
    if (query?.length > 1) {
      navigate(`/talent?search=${encodeURIComponent(query)}`);
    }
  };

  return (
    <>
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <button
            className={styles.topbarToggle}
            onClick={onSidebarToggle}
            aria-label="Toggle sidebar"
          >
            <ToggleIcon size={20} />
          </button>
          <span className={styles.topbarLogo}>
            <Logo color="#030659" />
          </span>
        </div>

        <div className={styles.topbarRight}>
          <div className={styles.topbarSearch}>
            <IconSearch size={18} />
            <input
              type="text"
              placeholder="Search for Talent"
              defaultValue=""
              onChange={(e) => {
                if (e.currentTarget.value.trim() === "") {
                  navigate("/talent");
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearchTalent(e.currentTarget.value);
                }
              }}
            />
          </div>

          <div className={styles.topbarActions}>
            {/* Mobile search button */}
            <button
              id="mobileSearchBtn"
              className={`${styles.topbarIconBtn} ${styles.mobileSearchBtn}`}
              aria-label="Search"
              onClick={onMobileSearchToggle}
            >
              <IconSearch size={18} />
            </button>

            {/* Bell + notification panel */}
            <div className={styles.notifWrap}>
              <button
                className={styles.topbarIconBtn}
                aria-label="Notifications"
                onClick={handleBellClick}
                data-unread-count={`${unreadCount + newCount} unread`}
                style={{ "--unread-color": hasUnread ? "#ff4d4f" : "#666" }}
              >
                <IconBell size={18} />
                {hasUnread && <span className={styles.notificationDot} />}
              </button>

              <Notifications
                notifications={processedNotifs}
                isOpen={notifOpen}
                onClose={() => setNotifOpen(false)}
                onMarkAllRead={handleMarkAllRead}
                onNotifClick={handleNotifClick}
                onSeeAll={handleSeeAll}
              />
            </div>

            {/* Avatar */}
            <Link
              to="/company-profile"
              className={styles.avatar}
              id="avatarLink"
            >
              <img
                src={data?.clientLogo || Image}
                alt="Company logo"
                className="w-full h-full object-cover rounded-full"
              />
            </Link>

            {/* Verified badge */}
            {data?.profileVerified && (
              <span className="center-flex flex items-center justify-center">
                <VerifiedIcon />
              </span>
            )}

            {/* Company name */}
            <span
              className={styles.topbarName}
              onClick={() => navigate("/company-profile")}
              style={{ cursor: "pointer" }}
            >
              {data?.companyName || "Your Company"}
            </span>
          </div>
        </div>
      </header>

      {/* Mobile search panel */}
      <div
        id="mobileSearchPanel"
        className={`${styles.mobileSearchPanel} ${mobileSearchOpen ? styles.mobileSearchPanelOpen : ""}`}
      >
        <div className={styles.mobileSearchInputWrap}>
          <IconSearch size={16} className={styles.searchPrefixIcon} />
          <input
            ref={mobileSearchInputRef}
            type="search"
            placeholder="Search for Talent"
            autoComplete="off"
            defaultValue=""
            onChange={(e) => {
              if (e.currentTarget.value.trim() === "") {
                navigate("/talent");
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearchTalent(e.currentTarget.value);
                onMobileSearchClose();
              }
              if (e.key === "Escape") onMobileSearchClose();
            }}
          />
          <button
            className={styles.mobileSearchSubmit}
            aria-label="Submit search"
            onClick={() => {
              handleSearchTalent(mobileSearchInputRef.current?.value);
              onMobileSearchClose();
            }}
          >
            <IconArrowRight size={14} />
          </button>
        </div>
        <button
          className={styles.mobileSearchCancel}
          onClick={onMobileSearchClose}
        >
          Cancel
        </button>
      </div>
    </>
  );
};

export default Header;
